if (!customElements.get('collection-themes')) {
  customElements.define(
    'collection-themes',
    class CollectionThemes extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          visual: '.collection-theme__visual',
          defaultGrid: '.collection-themes__grid--default',
          filteredGrid: '.collection-themes__grid--filtered',
          noResults: '.collection-themes__no-results',
          pagination: '.pagination',
        };

        this.paramKey = 'theme-colour';
        this.cachedItems = null;
        this.fetchPromise = null;
      }

      connectedCallback() {
        this.defaultGrid = this.querySelector(this.selectors.defaultGrid);
        this.filteredGrid = this.querySelector(this.selectors.filteredGrid);
        this.noResults = this.querySelector(this.selectors.noResults);
        this.pagination = this.querySelector(this.selectors.pagination);
        this.visuals = Array.from(this.querySelectorAll(this.selectors.visual));

        this.sortDefaultGrid();

        if (!this.filteredGrid || this.visuals.length === 0) return;

        this.visuals.forEach((visual) => {
          visual.setAttribute('role', 'button');
          visual.setAttribute('tabindex', '0');
          visual.setAttribute('aria-pressed', 'false');
          visual.addEventListener('click', this.onVisualClick.bind(this));
          visual.addEventListener('keydown', this.onVisualKeydown.bind(this));
        });

        const initialFilters = this.getFiltersFromUrl();
        if (initialFilters.length > 0) {
          this.setActiveFilters(initialFilters, { updateUrl: false, showLoading: true });
        }
      }

      sortDefaultGrid() {
        if (!this.defaultGrid) return;

        const items = Array.from(this.defaultGrid.querySelectorAll(':scope > .collection-themes__item'));
        if (items.length < 2) return;

        items.sort((a, b) => (Number(a.dataset.order) || 0) - (Number(b.dataset.order) || 0)).forEach((item) => this.defaultGrid.appendChild(item));
      }

      onVisualClick(event) {
        const visual = event.currentTarget;
        const colour = visual.dataset.colour;
        if (!colour) return;

        this.toggleFilter(colour);
      }

      onVisualKeydown(event) {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        this.onVisualClick(event);
      }

      toggleFilter(colour) {
        const filters = this.getActiveFilters();
        const index = filters.indexOf(colour);

        if (index > -1) {
          filters.splice(index, 1);
        } else {
          filters.push(colour);
        }

        this.setActiveFilters(filters);
      }

      getActiveFilters() {
        const value = this.getAttribute('data-active-filters') || '';
        return value
          .split(',')
          .map((filter) => filter.trim())
          .filter(Boolean);
      }

      getFiltersFromUrl() {
        const url = new URL(window.location.href);
        const value = url.searchParams.get(this.paramKey) || '';
        return value
          .split(',')
          .map((filter) => filter.trim().toLowerCase())
          .filter(Boolean);
      }

      setActiveFilters(filters, { updateUrl = true, showLoading = false } = {}) {
        const normalized = [...new Set(filters.map((filter) => filter.trim().toLowerCase()).filter(Boolean))];

        this.setAttribute('data-active-filters', normalized.join(','));
        this.syncVisualState(normalized);

        if (updateUrl) {
          this.updateUrl(normalized);
        }

        if (normalized.length === 0) {
          this.clearFilteredResults();
          return;
        }

        this.applyFilters(normalized, { showLoading });
      }

      syncVisualState(filters) {
        this.visuals.forEach((visual) => {
          const isActive = filters.includes(visual.dataset.colour);
          visual.classList.toggle('is-active', isActive);
          visual.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
      }

      updateUrl(filters) {
        const url = new URL(window.location.href);

        if (filters.length > 0) {
          url.searchParams.set(this.paramKey, filters.join(','));
        } else {
          url.searchParams.delete(this.paramKey);
        }

        history.replaceState({}, '', url.toString());
      }

      clearFilteredResults() {
        this.classList.remove('has-filters', 'is-loading');

        if (this.filteredGrid) {
          this.filteredGrid.innerHTML = '';
          this.filteredGrid.classList.add('hidden');
        }

        if (this.noResults) {
          this.noResults.classList.add('hidden');
        }
      }

      async applyFilters(filters, { showLoading = false } = {}) {
        this.classList.add('has-filters');

        if (showLoading || !this.cachedItems) {
          this.classList.add('is-loading');
          this.beforeRender();
        }

        try {
          const items = await this.getCachedItems();
          const matched = items.filter((item) => this.itemMatchesFilters(item, filters));

          this.renderFilteredResults(matched);
        } catch (error) {
          console.error('Collection themes filter failed:', error);
          this.renderFilteredResults([]);
        } finally {
          this.classList.remove('is-loading');
          this.afterRender();
        }
      }

      itemMatchesFilters(item, filters) {
        const colours = (item.dataset.colours || '')
          .split(',')
          .map((colour) => colour.trim().toLowerCase())
          .filter(Boolean);

        return filters.some((filter) => colours.includes(filter));
      }

      renderFilteredResults(items) {
        if (!this.filteredGrid) return;

        this.filteredGrid.innerHTML = '';

        if (items.length === 0) {
          this.filteredGrid.classList.add('hidden');
          this.noResults?.classList.remove('hidden');
          return;
        }

        const fragment = document.createDocumentFragment();
        items.forEach((item) => fragment.appendChild(item.cloneNode(true)));
        this.filteredGrid.appendChild(fragment);
        this.filteredGrid.classList.remove('hidden');
        this.noResults?.classList.add('hidden');
      }

      async getCachedItems() {
        if (this.cachedItems) return this.cachedItems;
        if (this.fetchPromise) return this.fetchPromise;

        this.fetchPromise = this.fetchItems()
          .then((items) => {
            this.cachedItems = items;
            return items;
          })
          .finally(() => {
            this.fetchPromise = null;
          });

        return this.fetchPromise;
      }

      async fetchItems() {
        const url = new URL(window.location.href);
        url.searchParams.set('view', 'collection-themes');
        url.searchParams.delete(this.paramKey);
        url.searchParams.delete('page');

        const response = await fetch(url.toString());
        if (!response.ok) throw new Error(`Failed to fetch collection themes (${response.status})`);

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return Array.from(doc.querySelectorAll('.collection-themes__item'));
      }

      beforeRender() {
        const translateY = FoxTheme.config.motionReduced ? 0 : 2.5;
        const target = this.filteredGrid || this;

        FoxTheme.Motion.animate(target, { y: translateY, opacity: 0 }, { duration: 0 });
      }

      afterRender() {
        const translateY = FoxTheme.config.motionReduced ? 0 : 2.5;
        const target = this.noResults && !this.noResults.classList.contains('hidden')
          ? this.noResults
          : this.filteredGrid;

        if (!target) return;

        FoxTheme.Motion.animate(
          target,
          { y: [translateY, 0], opacity: [0, 1] },
          { duration: FoxTheme.config.motionReduced ? 0 : 0.35 }
        );
      }
    }
  );
}
